import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/space-grotesk/500.css';
import './style.css';
import { GameEngine } from './GameEngine';
import { GameRenderer } from './GameRenderer';
import { InputController } from './InputController';
import {
  archiveStudy,
  clearSave,
  loadGame,
  loadJournal,
  loadSettings,
  saveGame,
  saveJournal,
  saveSettings,
} from './Persistence';
import type { GameSettings, SaveGame, TowerJournal } from './types';
import { formatTier } from './utils';

function element<T extends HTMLElement>(id: string): T {
  const target = document.getElementById(id);
  if (!target) throw new Error(`Missing #${id}`);
  return target as T;
}

const canvas = element<HTMLCanvasElement>('game-canvas');
const scoreNode = element('score');
const heightNode = element('height');
const nextNode = element('next');
const notificationNode = element('notification');
const dropHint = element('drop-hint');
const settingsButton = element<HTMLButtonElement>('settings-button');
const settingsDialog = element<HTMLDialogElement>('settings-dialog');
const resetDialog = element<HTMLDialogElement>('reset-dialog');
const resetButton = element<HTMLButtonElement>('reset-button');
const confirmReset = element<HTMLButtonElement>('confirm-reset');
const soundToggle = element<HTMLInputElement>('sound-toggle');
const hapticToggle = element<HTMLInputElement>('haptic-toggle');
const motionToggle = element<HTMLInputElement>('motion-toggle');
const nightToggle = element<HTMLInputElement>('night-toggle');
const journalButton = element<HTMLButtonElement>('journal-button');
const journalDialog = element<HTMLDialogElement>('journal-dialog');
const journalStudyNode = element('journal-study');
const journalHeightNode = element('journal-height');
const journalHighestNode = element('journal-highest');
const journalPrecisionNode = element('journal-precision');
const journalFlowNode = element('journal-flow');
const journalList = element<HTMLOListElement>('journal-list');
const themeColor = element<HTMLMetaElement>('theme-color');

const engine = new GameEngine();
const renderer = new GameRenderer(canvas);
let settings: GameSettings = loadSettings();
let journal: TowerJournal = loadJournal();
saveJournal(journal);
let audioContext: AudioContext | null = null;
let saveTimer = 0;
let notificationTimer = 0;
let latestCameraSaveBottom = 28;

function unlockAudio(): void {
  if (!settings.sound) return;
  audioContext ??= new AudioContext();
  if (audioContext.state === 'suspended') void audioContext.resume();
}

function playMergeSound(tier: number): void {
  if (!settings.sound || !audioContext) return;
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.value = Math.min(840, 220 + tier * 34);
  gain.gain.setValueAtTime(0.055, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.16);
  oscillator.connect(gain).connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.17);
}

function vibrate(pattern: number | number[]): void {
  if (settings.haptic && 'vibrate' in navigator) navigator.vibrate(pattern);
}

function scheduleSave(): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => void persist(), 700);
}

async function persist(): Promise<void> {
  const state = engine.serializeState();
  state.camera.bottom = latestCameraSaveBottom;
  state.camera.targetBottom = renderer.targetBottom;
  const save: SaveGame = {
    version: 4,
    savedAt: new Date().toISOString(),
    state,
  };
  try { await saveGame(save); } catch (error) { console.warn('Autosave unavailable', error); }
}

function showNotification(message: string, duration = 1_650): void {
  notificationNode.textContent = message;
  notificationNode.classList.add('visible');
  window.clearTimeout(notificationTimer);
  notificationTimer = window.setTimeout(
    () => notificationNode.classList.remove('visible'),
    duration,
  );
}

engine.onMerge = (event) => {
  renderer.addMergeParticle(event);
  playMergeSound(event.tier);
  vibrate(event.combo > 2 ? [8, 24, 8] : 8);
  showNotification(`COMBO ×${event.combo}`, 1_300);
  scheduleSave();
};

engine.onPrecision = (event) => {
  const bonus = BigInt(event.bonus).toLocaleString('en-US');
  showNotification(event.chain === 1
    ? `PRECISION LANDING +${bonus}`
    : `${event.flow} ×${event.chain} +${bonus}`);
  vibrate([6, 18, 10]);
  scheduleSave();
};

engine.onMilestone = (height) => {
  showNotification(`HEIGHT STUDY ${height} MODULES`, 2_200);
  scheduleSave();
};

const input = new InputController(canvas, renderer, {
  canDrop: () => engine.canDrop,
  onDrop: (worldX) => {
    if (engine.dropAt(worldX, renderer.spawnY())) {
      renderer.previewTier = engine.nextTier;
      vibrate(5);
      dropHint.classList.add('hidden');
      scheduleSave();
    }
  },
  onInteraction: unlockAudio,
});

function applySettings(): void {
  soundToggle.checked = settings.sound;
  hapticToggle.checked = settings.haptic;
  motionToggle.checked = settings.reducedMotion;
  nightToggle.checked = settings.nightMode;
  renderer.reducedMotion = settings.reducedMotion;
  renderer.nightMode = settings.nightMode;
  document.documentElement.classList.toggle('reduced-motion', settings.reducedMotion);
  document.documentElement.classList.toggle('night-mode', settings.nightMode);
  themeColor.content = settings.nightMode ? '#151713' : '#f6f2e9';
  saveSettings(settings);
}

function renderJournal(): void {
  const started = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(journal.currentStartedAt)).toUpperCase();
  journalStudyNode.textContent = `STUDY ${journal.currentStudyId.toString().padStart(3, '0')} / ${started}`;
  journalHeightNode.textContent = `${engine.progress.height} M`;
  journalHighestNode.textContent = formatTier(engine.progress.highestTier);
  journalPrecisionNode.textContent = engine.progress.precisionCount.toString();
  journalFlowNode.textContent = engine.progress.longestPrecisionChain.toString();
  journalList.replaceChildren();
  if (!journal.entries.length) {
    const empty = document.createElement('li');
    empty.className = 'journal-empty';
    empty.textContent = 'NO ARCHIVED STUDIES YET';
    journalList.append(empty);
    return;
  }
  for (const entry of journal.entries) {
    const item = document.createElement('li');
    const summary = document.createElement('span');
    const title = document.createElement('b');
    const detail = document.createElement('small');
    const highest = document.createElement('strong');
    title.textContent = `STUDY ${entry.id.toString().padStart(3, '0')} / ${entry.height} M`;
    detail.textContent = `${entry.precisionCount} PRECISION / FLOW ${entry.longestPrecisionChain}`;
    highest.textContent = formatTier(entry.highestTier);
    summary.append(title, detail);
    item.append(summary, highest);
    journalList.append(item);
  }
}

function updateHud(): void {
  scoreNode.textContent = BigInt(engine.progress.score).toLocaleString('en-US');
  heightNode.textContent = `${engine.progress.height} M`;
  nextNode.textContent = formatTier(engine.nextTier);
  renderer.previewTier = engine.nextTier;
  renderer.showPreview = engine.canDrop;
}

settingsButton.addEventListener('click', () => settingsDialog.showModal());
journalButton.addEventListener('click', () => {
  renderJournal();
  settingsDialog.close();
  journalDialog.showModal();
});
resetButton.addEventListener('click', () => resetDialog.showModal());
confirmReset.addEventListener('click', () => {
  journal = archiveStudy(journal, engine.progress);
  engine.reset();
  renderer.cameraBottom = 28;
  renderer.targetBottom = 28;
  void clearSave();
  updateHud();
});

soundToggle.addEventListener('change', () => { settings.sound = soundToggle.checked; applySettings(); });
hapticToggle.addEventListener('change', () => { settings.haptic = hapticToggle.checked; applySettings(); });
motionToggle.addEventListener('change', () => { settings.reducedMotion = motionToggle.checked; applySettings(); });
nightToggle.addEventListener('change', () => { settings.nightMode = nightToggle.checked; applySettings(); });

const resizeObserver = new ResizeObserver(() => renderer.resize());
resizeObserver.observe(canvas);

document.addEventListener('visibilitychange', () => {
  if (document.hidden) void persist();
});
window.addEventListener('pagehide', () => void persist());

let previousTime = performance.now();
let accumulator = 0;
let hudAccumulator = 0;
let archiveAccumulator = 0;
const fixedStep = 1000 / 120;

function frame(time: number): void {
  const elapsed = Math.min(50, time - previousTime);
  previousTime = time;
  accumulator += elapsed;
  hudAccumulator += elapsed;
  archiveAccumulator += elapsed;
  input.updateFrame();
  while (accumulator >= fixedStep) {
    engine.step(fixedStep);
    accumulator -= fixedStep;
  }
  renderer.updateCamera(engine.highestWorldY(), elapsed / 1000);
  const shift = engine.maybeRebase(renderer.cameraBottom);
  if (shift) {
    renderer.cameraBottom += shift;
    renderer.targetBottom += shift;
  }
  if (archiveAccumulator > 4_000) {
    engine.archiveDeepBodies(renderer.worldHeight);
    archiveAccumulator = 0;
  }
  latestCameraSaveBottom = renderer.cameraBottom;
  renderer.render(engine.blocks, engine.archivedChunks);
  if (hudAccumulator > 100) {
    updateHud();
    hudAccumulator = 0;
  }
  requestAnimationFrame(frame);
}

async function bootstrap(): Promise<void> {
  applySettings();
  try {
    const save = await loadGame();
    if (save) {
      engine.restoreState(save.state);
      renderer.cameraBottom = save.state.camera.bottom;
      renderer.targetBottom = save.state.camera.targetBottom;
    }
  } catch (error) {
    console.warn('Could not restore saved study', error);
  }
  renderer.previewX = input.state.previewWorldX;
  renderer.previewTier = engine.nextTier;
  updateHud();
  requestAnimationFrame(frame);
}

void bootstrap();
